import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { drawCardBack, preRenderLogo } from './cardBackRenderer';
import type { CardBackLogoType, CardBackStyle } from '../types';

/**
 * Card back settings for PDF generation.
 * When provided, card backs are rendered using native jsPDF drawing
 * which is MUCH faster than html2canvas.
 */
export interface CardBackOptions {
  enabled: boolean;
  logoType: CardBackLogoType;
  style: CardBackStyle;
  /** Border colors for each card, in the same order as the front cards */
  borderColors: string[];
}

/**
 * Configuration for PDF generation
 * Letter size: 8.5" x 11"
 * Card size: 2.5" x 3.5"
 * 3x3 grid per page = 9 cards per page
 */
const PDF_CONFIG = {
  // Page dimensions in inches
  pageWidth: 8.5,
  pageHeight: 11,
  // Card dimensions in inches
  cardWidth: 2.5,
  cardHeight: 3.5,
  // Grid layout
  columns: 3,
  rows: 3,
  cardsPerPage: 9,
  // Margins/gaps in inches
  horizontalGap: 0.25,
  verticalGap: 0,
  // Calculated margins to center the grid
  get marginLeft() {
    const totalCardWidth = this.columns * this.cardWidth + (this.columns - 1) * this.horizontalGap;
    return (this.pageWidth - totalCardWidth) / 2;
  },
  get marginTop() {
    const totalCardHeight = this.rows * this.cardHeight + (this.rows - 1) * this.verticalGap;
    return (this.pageHeight - totalCardHeight) / 2;
  },
  // Pixel dimensions at 96dpi for rendering
  cardWidthPx: 240,  // 2.5 * 96
  cardHeightPx: 336, // 3.5 * 96
  // Page dimensions in pixels at 96dpi (for batch rendering)
  pageWidthPx: 816,  // 8.5 * 96
  pageHeightPx: 1056, // 11 * 96
  // Margins in pixels
  get marginLeftPx() {
    return this.marginLeft * 96;
  },
  get marginTopPx() {
    return this.marginTop * 96;
  },
  horizontalGapPx: 24,  // 0.25 * 96
  verticalGapPx: 0,      // 0 * 96
};

/**
 * Creates a semi-transparent overlay to dim the page during PDF generation.
 * Users can still see the page behind it but cannot interact with it.
 * Returns the overlay element, an update function for progress, and a cleanup function.
 */
function createRenderingOverlay(): { overlay: HTMLElement; updateProgress: (progress: number) => void; cleanup: () => void } {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.7);
    z-index: 99998;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: all;
  `;
  
  const message = document.createElement('div');
  message.style.cssText = `
    background: #24160f;
    padding: 2rem 3rem;
    border-radius: 8px;
    font-family: 'Cinzel', 'Playfair Display', Georgia, serif;
    text-align: center;
    color: #f2e6d8;
    border: 1px solid rgba(139, 90, 43, 0.25);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  `;
  message.textContent = 'Generating PDF... 0%';
  overlay.appendChild(message);
  
  document.body.appendChild(overlay);
  
  return {
    overlay,
    updateProgress: (progress: number) => {
      message.textContent = `Generating PDF... ${Math.round(progress)}%`;
    },
    cleanup: () => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    },
  };
}

/**
 * Makes the print container and its parents visible for rendering.
 * The container is positioned off-screen so users don't see it.
 * Since we clone each element to a temp container for html2canvas rendering,
 * we just need the original elements to exist in the DOM (not display:none).
 * Returns a cleanup function to restore original styles.
 */
function makeContainerVisible(container: HTMLElement): () => void {
  const elementsToRestore: Array<{ element: HTMLElement; styles: Record<string, string> }> = [];
  
  // Walk up the DOM tree and make all parents visible
  let current: HTMLElement | null = container;
  while (current) {
    const computedStyle = window.getComputedStyle(current);
    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
      elementsToRestore.push({
        element: current,
        styles: {
          display: current.style.display,
          visibility: current.style.visibility,
          position: current.style.position,
          left: current.style.left,
          top: current.style.top,
          zIndex: current.style.zIndex,
          width: current.style.width,
          height: current.style.height,
          overflow: current.style.overflow,
          pointerEvents: current.style.pointerEvents,
        },
      });
      
      // Make visible but positioned off-screen so users don't see it
      // The actual rendering happens via cloned elements in cloneForRendering()
      current.style.display = 'block';
      current.style.visibility = 'visible';
      current.style.position = 'fixed';
      current.style.left = '-99999px';
      current.style.top = '-99999px';
      current.style.zIndex = '-1';
      current.style.overflow = 'visible';
      current.style.pointerEvents = 'none';
    }
    current = current.parentElement;
  }
  
  // Return cleanup function
  return () => {
    for (const { element, styles } of elementsToRestore) {
      element.style.display = styles.display;
      element.style.visibility = styles.visibility;
      element.style.position = styles.position;
      element.style.left = styles.left;
      element.style.top = styles.top;
      element.style.zIndex = styles.zIndex;
      element.style.width = styles.width;
      element.style.height = styles.height;
      element.style.overflow = styles.overflow;
      element.style.pointerEvents = styles.pointerEvents;
    }
  };
}

/**
 * Clones an element to a temporary container for rendering.
 * This ensures the element has proper dimensions regardless of its original container.
 * The container is positioned off-screen to prevent the user from seeing it.
 * Preserves inline styles (like border color) from the original element.
 */
function cloneForRendering(element: HTMLElement, width: number, height: number): { clone: HTMLElement; cleanup: () => void } {
  // Create a temporary container that's visible in the DOM but off-screen
  const tempContainer = document.createElement('div');
  tempContainer.style.cssText = `
    position: fixed;
    left: -9999px;
    top: -9999px;
    width: ${width}px;
    height: ${height}px;
    z-index: 99999;
    background: white;
    overflow: hidden;
    pointer-events: none;
    visibility: visible;
  `;
  
  // Deep clone the element
  const clone = element.cloneNode(true) as HTMLElement;
  
  // Preserve the original inline styles (like border color) and add our overrides
  // We need to keep existing styles and only override sizing/visibility
  const originalStyle = element.getAttribute('style') || '';
  
  // Preserve flexbox layout so margin-top:auto on card footers still works.
  // Non-flex elements (like the instructions page) fall back to block.
  const computedStyle = window.getComputedStyle(element);
  const isFlex = computedStyle.display === 'flex' || computedStyle.display === 'inline-flex';
  const displayOverride = isFlex
    ? `display: ${computedStyle.display} !important; flex-direction: ${computedStyle.flexDirection} !important;`
    : 'display: block !important;';
  
  // Build the new style string, preserving original styles but overriding specific properties
  const overrideStyles = `
    ${displayOverride}
    visibility: visible !important;
    width: ${width}px !important;
    height: ${height}px !important;
    min-width: ${width}px !important;
    min-height: ${height}px !important;
    max-width: ${width}px !important;
    max-height: ${height}px !important;
    margin: 0 !important;
    position: relative !important;
    opacity: 1 !important;
  `;
  
  // Append override styles to original styles so they take precedence
  clone.style.cssText = originalStyle + overrideStyles;
  
  tempContainer.appendChild(clone);
  document.body.appendChild(tempContainer);
  
  return {
    clone,
    cleanup: () => {
      if (tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
    },
  };
}

/**
 * Renders a DOM element to a canvas with high quality settings.
 * Clones the element to ensure proper dimensions.
 */
async function renderToCanvas(element: HTMLElement, width: number, height: number): Promise<HTMLCanvasElement> {
  // Clone the element to a temporary visible container
  const { clone, cleanup } = cloneForRendering(element, width, height);
  
  try {
    // Give browser time to layout the cloned element
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    const canvas = await html2canvas(clone, {
      scale: 2, // Higher resolution for better print quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width,
      height,
    });
    
    // Validate canvas has proper dimensions
    if (canvas.width === 0 || canvas.height === 0) {
      throw new Error('Failed to render element: canvas has zero dimensions');
    }
    
    return canvas;
  } finally {
    cleanup();
  }
}

/**
 * Draws cutting guide markers on the current PDF page.
 * Guides are small tick marks on the outer edges of the card grid only.
 * Vertical ticks appear on the top and bottom page edges at the left
 * and right side of every card. Horizontal ticks appear on the left
 * and right page edges at the top and bottom of every card. No
 * internal markers are drawn.
 */
function drawCuttingGuides(pdf: jsPDF) {
  const guideLength = 0.05; // length of each guide mark in inches
  const lineWidth = 0.01;

  const totalGridWidth = PDF_CONFIG.columns * PDF_CONFIG.cardWidth + (PDF_CONFIG.columns - 1) * PDF_CONFIG.horizontalGap;
  const totalGridHeight = PDF_CONFIG.rows * PDF_CONFIG.cardHeight + (PDF_CONFIG.rows - 1) * PDF_CONFIG.verticalGap;
  const gridLeft = PDF_CONFIG.marginLeft;
  const gridRight = PDF_CONFIG.marginLeft + totalGridWidth;
  const gridTop = PDF_CONFIG.marginTop;
  const gridBottom = PDF_CONFIG.marginTop + totalGridHeight;

  pdf.setDrawColor(128, 128, 128);
  pdf.setLineWidth(lineWidth);

  // X positions for the left and right edge of each card
  const cardEdgesX: number[] = [];
  for (let c = 0; c < PDF_CONFIG.columns; c++) {
    const left = PDF_CONFIG.marginLeft + c * (PDF_CONFIG.cardWidth + PDF_CONFIG.horizontalGap);
    cardEdgesX.push(left); // left edge
    cardEdgesX.push(left + PDF_CONFIG.cardWidth); // right edge
  }

  // Y positions for the top and bottom edge of each card
  const cardEdgesY: number[] = [];
  for (let r = 0; r < PDF_CONFIG.rows; r++) {
    const top = PDF_CONFIG.marginTop + r * (PDF_CONFIG.cardHeight + PDF_CONFIG.verticalGap);
    cardEdgesY.push(top); // top edge
    cardEdgesY.push(top + PDF_CONFIG.cardHeight); // bottom edge
  }

  // Vertical markers on top and bottom edges
  for (const x of cardEdgesX) {
    pdf.line(x, gridTop - guideLength, x, gridTop); // top edge
    pdf.line(x, gridBottom, x, gridBottom + guideLength); // bottom edge
  }

  // Horizontal markers on left and right edges
  for (const y of cardEdgesY) {
    pdf.line(gridLeft - guideLength, y, gridLeft, y); // left edge
    pdf.line(gridRight, y, gridRight + guideLength, y); // right edge
  }
}

/**
 * Generates a PDF from the print container element
 *
 * @param printRef - The ref to the hidden print container div
 * @param documentTitle - The title for the PDF file
 * @param includeInstructions - Whether to render the instructions page as page 1
 * @param cardBackOptions - Optional card back settings for native rendering
 * @param onProgress - Optional callback for progress updates
 */
export async function generatePdf(
  printRef: React.RefObject<HTMLDivElement | null>,
  documentTitle: string,
  includeInstructions: boolean,
  cardBackOptions?: CardBackOptions,
  onProgress?: (progress: number) => void
): Promise<void> {
  if (!printRef.current) {
    throw new Error('Print container not found');
  }

  const container = printRef.current;
  
  // Create overlay to hide rendering from user
  const { cleanup: cleanupOverlay, updateProgress: updateOverlayProgress } = createRenderingOverlay();
  
  // Make the entire container hierarchy visible for rendering
  const restoreVisibility = makeContainerVisible(container);
  
  try {
    // Give browser time to layout
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // Get the instructions page and the cards
    const instructionsElement = container.querySelector('.print-instructions') as HTMLElement | null;
    const printArea = container.querySelector('.print-area') as HTMLElement | null;
    
    if (!printArea) {
      throw new Error('Print area not found');
    }

    // Show/hide card backs instructions section based on whether backs are enabled
    const hasCardBacks = cardBackOptions?.enabled && cardBackOptions.borderColors.length > 0;
    const cardBacksInstructions = instructionsElement?.querySelector('.card-backs-instructions') as HTMLElement | null;
    let originalDisplay: string | null = null;
    if (cardBacksInstructions) {
      originalDisplay = cardBacksInstructions.style.display;
      cardBacksInstructions.style.display = hasCardBacks ? 'block' : 'none';
    }

    // Create PDF with letter size
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'in',
      format: 'letter',
    });

    const updateProgress = (value: number) => {
      const clampedValue = Math.min(value, 100);
      onProgress?.(clampedValue);
      updateOverlayProgress(clampedValue);
    };

    updateProgress(5);

    // Render instructions page first if it exists and is requested
    if (instructionsElement && includeInstructions) {
      // Instructions page dimensions: 8in x 10.5in at 96dpi = 768 x 1008 px
      const instructionsWidthPx = 768;
      const instructionsHeightPx = 1008;
      
      const instructionsCanvas = await renderToCanvas(instructionsElement, instructionsWidthPx, instructionsHeightPx);
      
      // Restore original display state
      if (cardBacksInstructions && originalDisplay !== null) {
        cardBacksInstructions.style.display = originalDisplay;
      }
      
      // Add instructions to PDF (full page)
      const imgData = instructionsCanvas.toDataURL('image/png');
      const imgAspectRatio = instructionsCanvas.width / instructionsCanvas.height;
      const pageAspectRatio = PDF_CONFIG.pageWidth / PDF_CONFIG.pageHeight;
      
      let imgWidth: number, imgHeight: number, imgX: number, imgY: number;
      
      if (imgAspectRatio > pageAspectRatio) {
        // Image is wider - fit to width
        imgWidth = PDF_CONFIG.pageWidth - 0.5; // 0.25" margin on each side
        imgHeight = imgWidth / imgAspectRatio;
        imgX = 0.25;
        imgY = (PDF_CONFIG.pageHeight - imgHeight) / 2;
      } else {
        // Image is taller - fit to height
        imgHeight = PDF_CONFIG.pageHeight - 0.5;
        imgWidth = imgHeight * imgAspectRatio;
        imgX = (PDF_CONFIG.pageWidth - imgWidth) / 2;
        imgY = 0.25;
      }
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth, imgHeight);
      pdf.addPage();
    }

    updateProgress(15);

    // Get card fronts from the print area
    // Cards are organized into .print-fronts container, or directly in print area
    const frontsContainer = printArea.querySelector('.print-fronts') as HTMLElement | null;
    
    // Get front card elements
    const frontElements = frontsContainer 
      ? Array.from(frontsContainer.children) as HTMLElement[]
      : Array.from(printArea.children) as HTMLElement[];
    
    if (frontElements.length === 0) {
      throw new Error('No cards to print');
    }

    // Calculate total pages for progress tracking
    const totalFrontPages = Math.ceil(frontElements.length / PDF_CONFIG.cardsPerPage);
    const totalBackPages = hasCardBacks ? Math.ceil(cardBackOptions!.borderColors.length / PDF_CONFIG.cardsPerPage) : 0;
    const totalPages = totalFrontPages + totalBackPages;
    let processedPages = 0;

    // Pre-render the logo if card backs are enabled (warms the cache)
    if (hasCardBacks) {
      // Pre-render both possible logo colors (for solid vs white style)
      const logoColor = cardBackOptions!.style === 'solid' ? '#ffffff' : cardBackOptions!.borderColors[0];
      await preRenderLogo(cardBackOptions!.logoType, logoColor);
    }

    /**
     * Renders front cards as individual images placed at exact grid positions.
     * This matches how backs are rendered so printers/ viewers handle both sides
     * identically and front/back alignment stays consistent.
     */
    async function renderFrontCards(cardElements: HTMLElement[], startOnNewPage: boolean) {
      if (cardElements.length === 0) return;

      if (startOnNewPage) {
        pdf.addPage();
      }
      drawCuttingGuides(pdf);

      for (let i = 0; i < cardElements.length; i++) {
        const cardIndexOnPage = i % PDF_CONFIG.cardsPerPage;

        // Start a new page at the beginning of each card group after the first
        if (i > 0 && cardIndexOnPage === 0) {
          pdf.addPage();
          drawCuttingGuides(pdf);
        }

        const card = cardElements[i];
        const col = cardIndexOnPage % PDF_CONFIG.columns;
        const row = Math.floor(cardIndexOnPage / PDF_CONFIG.columns);

        const x = PDF_CONFIG.marginLeft + col * (PDF_CONFIG.cardWidth + PDF_CONFIG.horizontalGap);
        const y = PDF_CONFIG.marginTop + row * (PDF_CONFIG.cardHeight + PDF_CONFIG.verticalGap);

        // Render the card individually so its PDF structure matches the backs
        const cardCanvas = await renderToCanvas(card, PDF_CONFIG.cardWidthPx, PDF_CONFIG.cardHeightPx);
        const imgData = cardCanvas.toDataURL('image/png');

        // Place the card at the same grid position used for backs
        pdf.addImage(imgData, 'PNG', x, y, PDF_CONFIG.cardWidth, PDF_CONFIG.cardHeight);

        // Update progress after each completed page
        const isLastCardOnPage = cardIndexOnPage === PDF_CONFIG.cardsPerPage - 1;
        const isLastCard = i === cardElements.length - 1;
        if (isLastCardOnPage || isLastCard) {
          processedPages++;
          const progressValue = 15 + (processedPages / totalPages) * 80;
          updateProgress(progressValue);
        }
      }
    }

    /**
     * Renders card backs using native jsPDF drawing.
     * This is MUCH faster than html2canvas because:
     * 1. Background/border are drawn with jsPDF primitives (instant)
     * 2. Logo is rendered once to canvas and cached as data URL
     * 3. All subsequent backs reuse the cached logo
     * 
     * For duplex printing, backs are mirrored horizontally so when the page
     * is flipped over, the back aligns with its front.
     */
    async function renderCardBacksNative(
      borderColors: string[],
      logoType: CardBackLogoType,
      style: CardBackStyle,
      startOnNewPage: boolean
    ) {
      if (borderColors.length === 0) return;

      if (startOnNewPage) {
        pdf.addPage();
      }
      drawCuttingGuides(pdf);

      const totalGroupPages = Math.ceil(borderColors.length / PDF_CONFIG.cardsPerPage);

      for (let pageIndex = 0; pageIndex < totalGroupPages; pageIndex++) {
        const startCardIndex = pageIndex * PDF_CONFIG.cardsPerPage;
        const endCardIndex = Math.min(startCardIndex + PDF_CONFIG.cardsPerPage, borderColors.length);
        const pageColors = borderColors.slice(startCardIndex, endCardIndex);

        // Draw each card back using native jsPDF (all 9 cards on this page)
        for (let i = 0; i < pageColors.length; i++) {
          const borderColor = pageColors[i];

          // Calculate position in the grid
          const row = Math.floor(i / PDF_CONFIG.columns);
          const col = i % PDF_CONFIG.columns;

          // Mirror horizontally for duplex printing: reverse the column order
          const mirroredCol = PDF_CONFIG.columns - 1 - col;

          const x = PDF_CONFIG.marginLeft + mirroredCol * (PDF_CONFIG.cardWidth + PDF_CONFIG.horizontalGap);
          const y = PDF_CONFIG.marginTop + row * (PDF_CONFIG.cardHeight + PDF_CONFIG.verticalGap);

          // Draw card back natively (fast!)
          await drawCardBack(pdf, x, y, borderColor, logoType, style);
        }

        // Update progress per page (not per card, since backs are instant)
        processedPages++;
        const progressValue = 15 + (processedPages / totalPages) * 80;
        updateProgress(progressValue);

        // Add new page if not the last page of this group
        if (pageIndex < totalGroupPages - 1) {
          pdf.addPage();
          drawCuttingGuides(pdf);
        }
      }
    }

    // Render cards with interleaved backs (if enabled)
    if (hasCardBacks) {
      // Interleave: fronts page 1, backs page 1, fronts page 2, backs page 2, etc.
      for (let pageIndex = 0; pageIndex < totalFrontPages; pageIndex++) {
        const startCardIndex = pageIndex * PDF_CONFIG.cardsPerPage;
        const endCardIndex = Math.min(startCardIndex + PDF_CONFIG.cardsPerPage, frontElements.length);
        const pageCards = frontElements.slice(startCardIndex, endCardIndex);
        const pageBackColors = cardBackOptions.borderColors.slice(startCardIndex, endCardIndex);
        
        // Render fronts for this page
        await renderFrontCards(pageCards, pageIndex > 0);
        
        // Render backs for this page (if we have any)
        if (pageBackColors.length > 0) {
          await renderCardBacksNative(
            pageBackColors,
            cardBackOptions.logoType,
            cardBackOptions.style,
            true
          );
        }
      }
    } else {
      // No card backs, just render all fronts
      await renderFrontCards(frontElements, false);
    }

    updateProgress(98);

    // Save the PDF
    const filename = `${documentTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
    pdf.save(filename);

    updateProgress(100);
  } finally {
    // Always restore visibility to hide the print container again
    restoreVisibility();
    cleanupOverlay();
  }
}
